import { useRef, useState } from 'react';
import { ImageDestinationEnum } from '@sportspredict/shared';
import { useCompressImageMutation } from '@/store/api';
import { validateEventImage } from '@/utils/event/event-image-validation';
import { useFileUpload } from './use-file.hook';

interface EventImageUpload {
	originalId: string;
	originalUrl: string;
	variants: Record<string, string>;
}

// Event banners have stricter requirements than avatars, logos and other uploads.
export function useEventImageUpload() {
	const [compressedImgData, setCompressedImgData] = useState<EventImageUpload | null>(null);
	const pendingRef = useRef<Promise<void> | null>(null);
	const { startUpload } = useFileUpload();
	const [compressImage] = useCompressImageMutation();

	const UploadImage = (file: File): Promise<void> => {
		if (pendingRef.current) return pendingRef.current;
		pendingRef.current = (async () => {
			try {
				await validateEventImage(file);
				const destination = ImageDestinationEnum.Events;
				const upload = await startUpload(file, destination, true);
				if (!upload) throw new Error('Another upload is in progress. Please wait.');
				const result = await compressImage({ imageId: upload.fileId, destination }).unwrap();
				if (!result?.originalId || !result?.originalUrl || !result?.variants?.xl) {
					throw new Error('The high-resolution image could not be processed. Please try again.');
				}
				setCompressedImgData(result);
			} finally {
				pendingRef.current = null;
			}
		})();
		return pendingRef.current;
	};

	return { UploadImage, compressedImgData };
}
